var __assign = this && this.__assign || function () {
  __assign = Object.assign || function (t) {
    for (var s, i = 1, n = arguments.length; i < n; i++) {
      s = arguments[i];
      for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
    }
    return t;
  };
  return __assign.apply(this, arguments);
};
var __spreadArray = this && this.__spreadArray || function (to, from, pack) {
  if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
    if (ar || !(i in from)) {
      if (!ar) ar = Array.prototype.slice.call(from, 0, i);
      ar[i] = from[i];
    }
  }
  return to.concat(ar || Array.prototype.slice.call(from));
};
import { createElement as _createElement } from "react";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Box, Divider, Grid, IconButton, Link, Stack, Switch, Tooltip, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import { Fragment, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Autocomplete, TimezoneSelect } from '../../common';
import { StableAutocomplete } from '../../common/autocomplete/stable';
import { CopyFromLocationDropdown } from '../common/CopyFromLocationDropdown';
import { StepContentPanel } from '../common/StepContentPanel';
import { TIME_24H, TIME_CLOSED, TIME_OPTIONS, buildRegionLabel } from './selectors';
import { ORDERED_DAYS } from './types';
var DayCircle = styled(Box, {
  shouldForwardProp: function (prop) {
    return prop !== 'available';
  }
})(function (_a) {
  var theme = _a.theme,
    available = _a.available;
  return {
    width: 32,
    height: 32,
    borderRadius: '50%',
    border: '2px solid',
    borderColor: available ? theme.palette.text.primary : theme.palette.text.disabled,
    backgroundColor: available ? theme.palette.text.primary : 'transparent',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0
  };
});
var DayInitial = styled(Typography, {
  shouldForwardProp: function (prop) {
    return prop !== 'available';
  }
})(function (_a) {
  var theme = _a.theme,
    available = _a.available;
  return {
    color: available ? theme.palette.background.paper : theme.palette.text.disabled,
    lineHeight: 1
  };
});
var MIDNIGHT = '00:00:00';
export var TimeSelect = function (_a) {
  var _b, _c;
  var value = _a.value,
    onChange = _a.onChange,
    placeholder = _a.placeholder,
    minTime = _a.minTime,
    isEndTime = _a.isEndTime,
    _d = _a.fullWidth,
    fullWidth = _d === void 0 ? false : _d,
    _e = _a.disabled,
    disabled = _e === void 0 ? false : _e;
  var t = useTranslation('orgConfig', {
    keyPrefix: 'hours'
  }).t;
  var baseOptions = minTime ? TIME_OPTIONS.filter(function (opt) {
    return opt.value > minTime;
  }) : TIME_OPTIONS;
  var timeOptions = (isEndTime ? baseOptions.filter(function (opt) {
    return opt.value !== MIDNIGHT;
  }) : baseOptions).map(function (opt) {
    return {
      id: opt.value,
      name: opt.label
    };
  });
  var midnightOpt = TIME_OPTIONS.find(function (opt) {
    return opt.value === MIDNIGHT;
  });
  var closedOption = {
    id: TIME_CLOSED,
    name: t('schedule.closed')
  };
  var hours24Option = {
    id: TIME_24H,
    name: t('schedule.hours24')
  };
  var midnightOption = {
    id: midnightOpt.value,
    name: midnightOpt.label
  };
  var options = __spreadArray(__spreadArray(__spreadArray([closedOption], isEndTime ? [] : [hours24Option], true), timeOptions, true), isEndTime ? [midnightOption] : [], true);
  var hasCurrentValue = value ? options.some(function (o) {
    return o.id === value;
  }) : true;
  var fallbackOption = null;
  if (value && !hasCurrentValue) {
    var matchedOption = TIME_OPTIONS.find(function (opt) {
      return opt.value === value;
    });
    fallbackOption = {
      id: value,
      name: (_b = matchedOption === null || matchedOption === void 0 ? void 0 : matchedOption.label) !== null && _b !== void 0 ? _b : value
    };
  }
  var finalOptions = fallbackOption ? __spreadArray([fallbackOption], options, true) : options;
  var firstTimeOptionId = (_c = timeOptions[0]) === null || _c === void 0 ? void 0 : _c.id;
  return _jsx(StableAutocomplete, {
    size: "small",
    disabled: disabled,
    sx: fullWidth ? {
      minWidth: '8rem'
    } : {
      width: '8rem'
    },
    options: finalOptions,
    value: value !== null && value !== void 0 ? value : undefined,
    getOptionValue: function (o) {
      return o.id;
    },
    getOptionLabel: function (o) {
      return o.name;
    },
    isOptionEqualToValue: function (o, v) {
      return o.id === v.id;
    },
    placeholder: placeholder,
    onChange: function (newValue) {
      if (!newValue) return;
      onChange(newValue);
    },
    renderOption: function (liProps, option) {
      return option.id === firstTimeOptionId ? _jsxs(Fragment, {
        children: [_jsx(Divider, {}), _jsx("li", __assign({}, liProps, {
          children: option.name
        }))]
      }, option.id) : _createElement("li", __assign({}, liProps, {
        key: option.id
      }), option.name);
    }
  });
};
var DayRow = function (_a) {
  var day = _a.day,
    state = _a.state,
    onMakeAvailable = _a.onMakeAvailable,
    onMakeUnavailable = _a.onMakeUnavailable,
    onStartChange = _a.onStartChange,
    onEndChange = _a.onEndChange,
    onCopyClick = _a.onCopyClick;
  var t = useTranslation('orgConfig', {
    keyPrefix: 'hours'
  }).t;
  var hasTimeError = state.isAvailable && !!state.startTime && !!state.endTime && state.endTime !== MIDNIGHT && state.startTime >= state.endTime;
  return _jsxs(Box, {
    children: [_jsxs(Stack, __assign({
      direction: "row",
      alignItems: "center",
      spacing: 2,
      sx: {
        py: 2
      }
    }, {
      children: [_jsx(DayCircle, __assign({
        available: state.isAvailable
      }, {
        children: _jsx(DayInitial, __assign({
          variant: "caption",
          fontWeight: "bold",
          available: state.isAvailable
        }, {
          children: day.initial
        }))
      })), state.isAvailable ? _jsxs(_Fragment, {
        children: [_jsx(TimeSelect, {
          value: state.startTime,
          onChange: onStartChange,
          placeholder: t('schedule.startTime')
        }), _jsx(Typography, __assign({
          color: "text.secondary"
        }, {
          children: "\u2013"
        })), _jsx(TimeSelect, {
          value: state.endTime,
          onChange: onEndChange,
          placeholder: t('schedule.endTime'),
          minTime: state.startTime,
          isEndTime: true
        }), _jsx(Tooltip, __assign({
          title: t('schedule.markUnavailable')
        }, {
          children: _jsx(IconButton, __assign({
            size: "small",
            onClick: onMakeUnavailable,
            "aria-label": t('schedule.markDayUnavailable', {
              day: day.label
            }),
            sx: {
              color: 'text.primary',
              px: 2
            }
          }, {
            children: _jsx(FontAwesomeIcon, {
              icon: "xmark"
            })
          }))
        })), _jsx(Tooltip, __assign({
          title: t('schedule.copyToOtherDays', {
            day: day.label
          })
        }, {
          children: _jsx("span", {
            children: _jsx(IconButton, __assign({
              size: "small",
              onClick: onCopyClick,
              disabled: !state.startTime || !state.endTime,
              "aria-label": t('schedule.copyHours', {
                day: day.label
              }),
              sx: {
                color: 'text.primary',
                px: 2
              }
            }, {
              children: _jsx(FontAwesomeIcon, {
                icon: "copy"
              })
            }))
          })
        }))]
      }) : _jsxs(Stack, __assign({
        direction: "row",
        alignItems: "center",
        sx: {
          minHeight: 40
        },
        spacing: 2,
        padding: 1
      }, {
        children: [_jsx(Typography, __assign({
          color: "text.disabled"
        }, {
          children: t('schedule.unavailable')
        })), _jsx(Tooltip, __assign({
          title: t('schedule.setHours')
        }, {
          children: _jsx(IconButton, __assign({
            size: "small",
            onClick: onMakeAvailable,
            "aria-label": t('schedule.addHours', {
              day: day.label
            })
          }, {
            children: _jsx(FontAwesomeIcon, {
              icon: "circle-plus"
            })
          }))
        }))]
      }))]
    })), hasTimeError && _jsx(Typography, __assign({
      variant: "caption",
      color: "error",
      sx: {
        pl: 6
      }
    }, {
      children: t('schedule.timeError')
    }))]
  });
};
export var LocationConfigPanel = function (_a) {
  var currentForm = _a.currentForm,
    selectedLocationId = _a.selectedLocationId,
    allLocations = _a.allLocations,
    regions = _a.regions,
    currentCountryCode = _a.currentCountryCode,
    holidayCount = _a.holidayCount,
    updateForm = _a.updateForm,
    updateDay = _a.updateDay,
    onCopyDayClick = _a.onCopyDayClick,
    onOpenHolidaysModal = _a.onOpenHolidaysModal,
    onCopyFrom = _a.onCopyFrom,
    onClear = _a.onClear,
    _b = _a.isCopying,
    isCopying = _b === void 0 ? false : _b;
  var _c = useTranslation('orgConfig', {
      keyPrefix: 'hours'
    }),
    t = _c.t,
    i18n = _c.i18n;
  var holidaysSelected = holidayCount > 0;
  var regionOptions = useMemo(function () {
    return __spreadArray([{
      id: "".concat(currentCountryCode, "::"),
      name: buildRegionLabel(currentCountryCode, '', regions, i18n === null || i18n === void 0 ? void 0 : i18n.language)
    }], regions.map(function (r) {
      return {
        id: "".concat(currentCountryCode, "::").concat(r.code),
        name: buildRegionLabel(currentCountryCode, r.code, regions, i18n === null || i18n === void 0 ? void 0 : i18n.language)
      };
    }), true);
  }, [currentCountryCode, regions, i18n === null || i18n === void 0 ? void 0 : i18n.language]);
  return _jsxs(Grid, __assign({
    container: true,
    spacing: 6
  }, {
    children: [_jsx(Grid, __assign({
      item: true,
      xs: 12,
      md: 7
    }, {
      children: _jsx(StepContentPanel, __assign({
        title: t('schedule.title'),
        description: t('schedule.description')
      }, {
        children: _jsx(Stack, {
          children: ORDERED_DAYS.map(function (d) {
            var dayState = currentForm.days.find(function (ds) {
              return ds.dayOfWeek === d.key;
            });
            if (!dayState) return null;
            var day = {
              key: d.key,
              label: t(d.labelKey),
              initial: t(d.initialKey)
            };
            return _jsx(DayRow, {
              day: day,
              state: dayState,
              onMakeAvailable: function () {
                return updateDay(d.key, function (ds) {
                  return __assign(__assign({}, ds), {
                    isAvailable: true
                  });
                });
              },
              onMakeUnavailable: function () {
                return updateDay(d.key, function () {
                  return {
                    dayOfWeek: d.key,
                    isAvailable: false,
                    startTime: null,
                    endTime: null
                  };
                });
              },
              onStartChange: function (val) {
                if (val === TIME_CLOSED) {
                  updateDay(d.key, function () {
                    return {
                      dayOfWeek: d.key,
                      isAvailable: false,
                      startTime: null,
                      endTime: null
                    };
                  });
                } else if (val === TIME_24H) {
                  updateDay(d.key, function (ds) {
                    return __assign(__assign({}, ds), {
                      startTime: MIDNIGHT,
                      endTime: MIDNIGHT
                    });
                  });
                } else {
                  updateDay(d.key, function (ds) {
                    return __assign(__assign({}, ds), {
                      startTime: val
                    });
                  });
                }
              },
              onEndChange: function (val) {
                if (val === TIME_CLOSED) {
                  updateDay(d.key, function () {
                    return {
                      dayOfWeek: d.key,
                      isAvailable: false,
                      startTime: null,
                      endTime: null
                    };
                  });
                } else {
                  updateDay(d.key, function (ds) {
                    return __assign(__assign({}, ds), {
                      endTime: val
                    });
                  });
                }
              },
              onCopyClick: function () {
                return onCopyDayClick(d.key);
              }
            }, d.key);
          })
        })
      }))
    })), _jsxs(Grid, __assign({
      item: true,
      xs: 12,
      md: 5
    }, {
      children: [_jsx(Box, __assign({
        sx: {
          display: 'flex',
          justifyContent: 'flex-end',
          mb: 5
        }
      }, {
        children: _jsx(CopyFromLocationDropdown, {
          locations: allLocations,
          currentLocationId: selectedLocationId,
          onCopy: onCopyFrom,
          onClear: onClear,
          disabled: isCopying
        })
      })), _jsx(Typography, __assign({
        variant: "subtitle2",
        gutterBottom: true
      }, {
        children: t('timezone.title')
      })), _jsx(Box, __assign({
        sx: {
          mb: 8
        }
      }, {
        children: _jsx(TimezoneSelect, {
          value: currentForm.timezone,
          onChange: function (val) {
            return updateForm(selectedLocationId, function (prev) {
              return __assign(__assign({}, prev), {
                timezone: val
              });
            });
          },
          sx: {
            maxWidth: '16rem'
          }
        })
      })), _jsx(Typography, __assign({
        variant: "subtitle2",
        gutterBottom: true
      }, {
        children: t('holidays.title')
      })), _jsx(Typography, __assign({
        gutterBottom: true
      }, {
        children: t('holidays.description')
      })), _jsxs(Stack, __assign({
        direction: "row",
        alignItems: "center",
        spacing: 1,
        sx: {
          mb: 1.5
        }
      }, {
        children: [_jsx(Autocomplete, {
          size: "small",
          sx: {
            maxWidth: '16rem'
          },
          options: regionOptions,
          value: "".concat(currentForm.holiday.countryCode, "::").concat(currentForm.holiday.regionCode),
          getOptionValue: function (o) {
            return o.id;
          },
          getOptionLabel: function (o) {
            return o.name;
          },
          isOptionEqualToValue: function (o, v) {
            return o.id === v.id;
          },
          onChange: function (newValue) {
            if (!newValue) return;
            var parts = newValue.split('::');
            updateForm(selectedLocationId, function (prev) {
              return __assign(__assign({}, prev), {
                holiday: __assign(__assign({}, prev.holiday), {
                  countryCode: parts[0],
                  regionCode: parts[1],
                  customHolidays: []
                })
              });
            });
          }
        }), _jsx(Switch, {
          checked: currentForm.holiday.autoApplyHolidays,
          onChange: function (e) {
            var checked = e.target.checked;
            updateForm(selectedLocationId, function (prev) {
              return __assign(__assign({}, prev), {
                holiday: __assign(__assign({}, prev.holiday), {
                  autoApplyHolidays: checked,
                  customHolidays: checked ? prev.holiday.customHolidays : []
                })
              });
            });
          }
        })]
      })), _jsx(Link, __assign({
        component: "button",
        onClick: onOpenHolidaysModal,
        sx: {
          fontWeight: holidaysSelected ? 'bold' : 'normal'
        }
      }, {
        children: holidaysSelected ? t('holidays.selectHolidaysCount', {
          count: holidayCount
        }) : t('holidays.selectHolidays')
      }))]
    }))]
  }));
};